import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import { useLanguage } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-navy/15 bg-warm px-4 py-3 text-sm font-semibold text-ink placeholder-mute/70 transition-[border-color,box-shadow] duration-300 focus:border-sky focus:outline-none focus:ring-2 focus:ring-sky/20";

export default function ValuationModal({ open, onOpenChange }) {
  const { lang, t } = useLanguage();
  const v = t.valuation;
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", location: "", property_type: "", message: "" });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/valuations`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        location: form.location,
        property_type: form.property_type || v.types[0],
        message: form.message || null,
        locale: lang,
      });
      toast.success(v.success);
      setForm({ name: "", email: "", phone: "", location: "", property_type: "", message: "" });
      onOpenChange(false);
    } catch {
      toast.error(v.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="valuation-modal"
        className="max-h-[90vh] overflow-y-auto rounded-[2rem] border-navy/10 bg-warm p-0 sm:max-w-xl"
      >
        <div className="bg-navy px-8 py-8 sm:px-10">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-semibold text-white">{v.title}</DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-white/65">{v.sub}</DialogDescription>
          </DialogHeader>
        </div>

        <form data-testid="valuation-form" onSubmit={submit} className="grid gap-5 px-8 py-8 sm:grid-cols-2 sm:px-10">
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{v.name}</span>
            <input data-testid="valuation-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder={v.namePh} className={inputCls} />
          </label>
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{v.email}</span>
            <input data-testid="valuation-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={v.emailPh} className={inputCls} />
          </label>
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{v.phone}</span>
            <input data-testid="valuation-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder={v.phonePh} className={inputCls} />
          </label>
          <label className="grid gap-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{v.type}</span>
            <select data-testid="valuation-type-select" required value={form.property_type} onChange={set("property_type")} className={inputCls}>
              <option value="" disabled>
                —
              </option>
              {v.types.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>
          <label className="grid gap-2 sm:col-span-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{v.location}</span>
            <input data-testid="valuation-location-input" required minLength={2} value={form.location} onChange={set("location")} placeholder={v.locationPh} className={inputCls} />
          </label>
          <label className="grid gap-2 sm:col-span-2">
            <span className="text-xs font-extrabold uppercase tracking-[0.15em] text-navy">{v.message}</span>
            <textarea data-testid="valuation-message-input" rows={3} value={form.message} onChange={set("message")} placeholder={v.messagePh} className={`${inputCls} resize-none`} />
          </label>
          <button data-testid="valuation-submit-button" type="submit" disabled={sending} className="btn-primary mt-1 w-full disabled:opacity-60 sm:col-span-2">
            {sending && <Loader2 className="h-4 w-4 animate-spin" />}
            {sending ? v.sending : v.submit}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
